import { useEffect, useLayoutEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router';
import { Toast, ToastRegion } from '../ui/Toast';
import { CheckoutHost } from '../features/beat/CheckoutHost';
import { BottomNav } from './BottomNav';
import { Header } from './Header';
import { useAppStore } from './store';
import { tabOf } from './tabs';
import styles from './Shell.module.css';

/** Remembers the path per tab and the scroll position per path (DESIGN_SYSTEM §5). */
function useContinuity() {
  const main = useRef<HTMLElement>(null);
  const { pathname, search, hash } = useLocation();
  const visit = useAppStore((s) => s.visit);
  const rememberScroll = useAppStore((s) => s.rememberScroll);

  useEffect(() => {
    const tab = tabOf(pathname);
    if (tab) visit(tab, pathname + search);
  }, [pathname, search, visit]);

  useLayoutEffect(() => {
    const el = main.current;
    if (!el) return;
    // A search result's #h-... anchor wins over the remembered scroll
    // (LearnTabLayout scrolls to it once the heading exists).
    if (!hash) el.scrollTop = useAppStore.getState().continuity.scroll[pathname] ?? 0;
    let t: ReturnType<typeof setTimeout> | undefined;
    const onScroll = () => {
      clearTimeout(t);
      t = setTimeout(() => rememberScroll(pathname, el.scrollTop), 200);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      clearTimeout(t);
      el.removeEventListener('scroll', onScroll);
    };
  }, [pathname, hash, rememberScroll]);

  return main;
}

function Toasts() {
  const toasts = useAppStore((s) => s.toasts);
  const dismiss = useAppStore((s) => s.dismissToast);
  return (
    <ToastRegion active={toasts.length > 0}>
      {toasts.map((t) => (
        <Toast key={t.id} message={t.message} action={t.action} duration={t.duration} onDismiss={() => dismiss(t.id)} />
      ))}
    </ToastRegion>
  );
}

export function Layout() {
  const main = useContinuity();
  return (
    <div className={styles.shell}>
      <a href="#main" className={styles.skip}>Skip to content</a>
      <Header />
      <main id="main" ref={main} className={styles.main} tabIndex={-1}>
        <Outlet />
      </main>
      <BottomNav />
      <CheckoutHost />
      <Toasts />
    </div>
  );
}
